import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid2, IconButton, TextField, Typography, Autocomplete, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppQuery } from '../../../services/appQuery';
import { getTaxesURL, TaxListResponse, TaxItem, assignTaxURL } from './PackageConfigHelper';
import { getRequest, postRequest, putRequest } from '../../../services/httpService';
import { billingConfigResponse, getbillingConfigURL } from './PackageDefinition.helper';
import { ReactComponent as DeleteIcon } from '../../../assets/SVG/trash.svg';
import { useAlert } from '../../../components/alertProvider';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  onClose: () => void;
  packageName?: string;
  packageId?: number;
}

type TaxRow = { id?: number; tax: number; tax_name: string; type: string; percent: number };

const ApplyTaxModal: React.FC<Props> = ({ open, onClose, packageName, packageId }) => {
  const [tax, setTax] = useState<TaxItem | null>(null);
  const [taxType, setTaxType] = useState<string>('');
  const [percent, setPercent] = useState<number>(0);
  const [rows, setRows] = useState<TaxRow[]>([]);
  const [submitPayload, setSubmitPayload] = useState<{ tax_items: { id?: number; tax: number; tax_type: string; value: string }[] } | null>(null);
  const { addAlert } = useAlert();
  const { t: tBilling } = useTranslation('billing_config');
  const [highlightTaxId, setHighlightTaxId] = useState<number | null>(null);

  const { data: taxList } = useAppQuery<TaxListResponse>(
    ['taxlist'],
    () => getRequest<TaxListResponse>(`${getTaxesURL()}`),
    { 
      enabled: true,
      staleTime: 5 * 60 * 1000
    }
  );
  
  const { data: billingConfig } = useAppQuery<billingConfigResponse>(
    ['billingConfig'],
    () => getRequest<billingConfigResponse>(getbillingConfigURL()),
    { staleTime: 5 * 60 * 1000 }
  );

  // Fetch already assigned taxes for the package and prefill rows
  type AssignedTaxResponse = { tax_items: Array<{ id?: number; tax: number; tax_type: string; value: string | number }> } | null;
  const { data: assignedTaxes } = useAppQuery<AssignedTaxResponse>(
    ['assignedPackageTaxes', packageId],
    () => getRequest<AssignedTaxResponse>(assignTaxURL(packageId as number)),
    { enabled: Boolean(packageId) && Boolean(open), staleTime: 0 }
  );

  const clearAll = () => {
    setTax(null);
    setTaxType('');
    setPercent(0);
  };

  const clearRows = () => setRows([]);

  const removeRow = (index: number) => {
    setRows(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleClose = () => {
    clearAll();
    clearRows();
    onClose();
  };

  useEffect(() => {
    if (!assignedTaxes) return;
    const items: Array<{ id?: number; tax: number; tax_type: string; value: string | number }> = assignedTaxes?.tax_items ?? [];
    const mapped = items.map(it => {
      const taxId = Number(it.tax);
      const name = (taxList?.data ?? []).find(t => t.id === taxId)?.name ?? '';
      const percent = Number(typeof it.value === 'string' ? parseFloat(it.value) : it.value || 0);
      return {
        id: it.id,
        tax: taxId,
        tax_name: name,
        type: it.tax_type,
        percent: Number(percent.toFixed(2))
      } as TaxRow;
    });
    setRows(mapped);
  }, [assignedTaxes, taxList]);

  const addRow = () => {
    if (!tax || !taxType || percent <= 0) return;
    // Prevent duplicate tax by tax_id
    const exists = rows.some(r => r.tax === tax.id);
    if (exists) {
      setHighlightTaxId(tax.id);
      addAlert(tBilling('package_config.messages.tax_already_added', { defaultValue: 'This tax is already added.' }), 'warning');
      // remove highlight after a short delay
      setTimeout(() => setHighlightTaxId(null), 1500);
      return;
    }
    setRows(prev => [...prev, { tax: tax.id, tax_name: tax.name, type: taxType, percent: Number(percent) }]);
    clearAll();
    setTax(null);
    setTaxType('');
  };

  const buildPayload = () => {
    const items = rows.map(r => {
      const typeCode = (billingConfig?.TAX_TYPE ?? []).find(t => t.name === r.type)?.code || r.type;
      return {
        ...(r.id ? { id: r.id } : {}),
        tax: r.tax,
        tax_type: typeCode,
        value: Number(r.percent).toFixed(2),
      };
    });
    return { tax_items: items };
  };

  const handleSave = () => {
    const payload = buildPayload();
    setSubmitPayload(payload);

  };

  const hasExistingAssigned = (assignedTaxes?.tax_items?.length ?? 0) > 0;
  const { isSuccess: assignSuccess, isError: assignError } = useAppQuery(
    ['assignPackageTax', packageId, submitPayload],
    () => {
      const url = assignTaxURL(packageId as number);
      const body = submitPayload as unknown as Record<string, unknown>;
      return hasExistingAssigned ? putRequest(url, body) : postRequest(url, body);
    },
    {
      enabled: Boolean(packageId) && Boolean(submitPayload),
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  React.useEffect(() => {
    if (!submitPayload) return;
    if (assignSuccess) {
      addAlert(
        hasExistingAssigned
          ? tBilling('package_config.messages.tax_updated_success', { defaultValue: 'Tax updated successfully.' })
          : tBilling('package_config.messages.tax_assigned_success', { defaultValue: 'Tax assigned successfully.' }),
        'success'
      );
      clearAll();
      clearRows();
      onClose();
      setSubmitPayload(null);
    } else if (assignError) {
      addAlert(
        hasExistingAssigned
          ? tBilling('package_config.messages.tax_update_failed', { defaultValue: 'Failed to update tax.' })
          : tBilling('package_config.messages.tax_assign_failed', { defaultValue: 'Failed to assign tax.' }),
        'error'
      );
      setSubmitPayload(null);
    }
  }, [assignSuccess, assignError, submitPayload, addAlert, onClose, hasExistingAssigned, tBilling]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }} data-testid="apply-tax-modal">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{tBilling('package_config.apply_tax', { defaultValue: 'Apply Tax' })} <span style={{fontWeight:400}}>{packageName ? `(${packageName})` : ''}</span></Typography>
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontWeight: 600, mb: 2 }}>{tBilling('package_config.tax_declaration', { defaultValue: 'Tax Declaration' })}</Typography>
        <Grid2 container spacing={2} alignItems="center" >
          <Grid2 size={'auto'} sx={{ minWidth: 200 }}>
            <Autocomplete
              fullWidth
              size="small"
              options={(taxList?.data ?? []) as TaxItem[]}
              getOptionLabel={(opt) => opt?.name ?? ''}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              value={tax}
              onChange={(_, option) => setTax(option)}
              renderInput={(params) => (
                <TextField {...params} placeholder={tBilling('package_config.placeholders.select_tax', { defaultValue: 'Select Tax' })} />
              )}
            />
          </Grid2>
          <Grid2 size={'auto'} sx={{ minWidth: 200 }}>
            <Autocomplete
              fullWidth
              size="small"
              options={(billingConfig?.TAX_TYPE ?? [])}
              getOptionLabel={(opt) => opt?.name ?? ''}
              isOptionEqualToValue={(opt, val) => opt?.code === val?.code}
              value={(billingConfig?.TAX_TYPE ?? []).find(opt => opt?.name === taxType) || null}
              onChange={(_, option) => setTaxType( option?.name ?? '')}
              renderInput={(params) => (
                <TextField {...params} placeholder={tBilling('package_config.placeholders.select_tax_type', { defaultValue: 'Select Tax Type' })} />
              )}
            />
          </Grid2>
          <Grid2 size={'auto'} sx={{ minWidth: 200 }}>{/* fills remaining space */}
            <TextField fullWidth size="small" placeholder={tBilling('package_config.placeholders.enter_percentage', { defaultValue: 'Enter Percentage' })} type="number" inputProps={{ min: 0, max: 100, step: 'any' }} value={percent || ''} onChange={e => setPercent(Number(e.target.value) || 0)} />
          </Grid2>
          <Grid2 size={'auto'}>
            <Button variant="outlined" color="inherit" onClick={clearAll}>{tBilling('package_config.clear_all', { defaultValue: 'Clear All' })}</Button>
          </Grid2>
          <Grid2 size={'auto'}>
            <Button variant="contained" sx={{ backgroundColor: '#444', '&:hover': { backgroundColor: '#222' } }} onClick={addRow}>{tBilling('package_config.add', { defaultValue: 'Add' })}</Button>
          </Grid2>
        </Grid2>

        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>{tBilling('package_config.taxation_details_list', { defaultValue: 'Taxation Details List' })}</Typography>
          <TableContainer sx={{ border: '1px solid #eee', borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>{tBilling('package_config.tax', { defaultValue: 'Tax' })}</TableCell>
                  <TableCell>{tBilling('package_config.tax_type', { defaultValue: 'Tax Type' })}</TableCell>
                  <TableCell align="center">{tBilling('package_config.percentage', { defaultValue: 'Percentage(%)' })}</TableCell>
                  <TableCell align="center">{tBilling('package_config.action', { defaultValue: 'Action' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell>
                      <Typography color="text.secondary">-</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color="text.secondary">-</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography color="text.secondary">-</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography color="text.secondary">-</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r, idx) => (
                    <TableRow
                      key={idx}
                      hover
                      sx={{
                        transition: 'background-color 0.3s ease',
                        backgroundColor: highlightTaxId === r.tax ? 'rgba(255, 235, 59, 0.25)' : 'inherit',
                      }}
                    >
                      <TableCell>{r.tax_name}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{r.type}</TableCell>
                      <TableCell align="center">{Number(r.percent).toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => removeRow(idx)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
          <Button variant="outlined" onClick={handleClose}>{tBilling('package_config.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#444', '&:hover': { backgroundColor: '#222' } }}
            onClick={() => {
              handleSave();
             
            }}
          >
            {tBilling('package_config.save', { defaultValue: 'Save' })}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyTaxModal;
